import { format as numFormat } from '@awp/helpers/numFormat';
import { subDays } from 'date-fns';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import { externalTooltipHandler } from '@graphs/SingleStation/TooltipHelper';
import { defaultBuildOptions, buildXAxis, yAxisDefaultConfig, fillObservationData } from '@graphs/SingleStation/Helper';
import { mapArrayToObjectReducer, groupBy } from '@awp/helpers/Array';

const temperatureUnit = '°C';

//externally called by other pages, frost probe, graph
export const calculateFrostProbeStartDate = (endDate, range) => {
  return subDays(new Date(endDate), range).getTime();

  // if (isToday(endDate)) {
  // 	return subDays(new Date(endDate), range + 1).getTime();
  // }

  // return startOfDay(subDays(new Date(endDate), range)).getTime();
};

export const buildFrostProbeOptions = (fpObservations, graphId, showInvalidData, observationHour) => {
  if (fpObservations) {
    //code to determine the min/max x-axis values for graph display
    const maxX = Math.max(
      ...Object.values(fpObservations)
        .flat()
        .map((o) => o.y),
    );
    const minX = Math.min(
      ...Object.values(fpObservations)
        .flat()
        .map((o) => o.y),
    );

    const maxY =
      Math.max(
        ...Object.values(fpObservations)
          .flat()
          .map((o) => o.elevation),
      ) / 10;
    const minY =
      Math.min(
        ...Object.values(fpObservations)
          .flat()
          .map((o) => o.elevation),
      ) / 10;

    return {
      ...defaultBuildOptions,
      showInvalidData: showInvalidData,
      id: graphId,
      frostProbe: true,
      scales: {
        xAxis: {
          ...buildXAxis(Math.floor(minX), Math.ceil(maxX), 'linear'),
          ticks: { beginAtZero: true }, //override the ticks element, we'll let chartjs handle it but always include zero
          title: {
            text: `Temperature (${temperatureUnit})`,
            display: true,
          },
        },
        yAxis: {
          ...yAxisDefaultConfig,
          type: 'linear',
          Id: 'yAxis-FP',
          suggestedMin: Math.floor(minY),
          suggestedMax: Math.ceil(maxY),
          title: {
            text: 'Depth (cm)',
            display: true,
          },
        },
      },
      plugins: {
        tooltip: {
          enabled: false,
          //position: 'nearest',
          position: 'myCustomPositioner',
          external: externalTooltipHandler,
        },
        legend: {
          align: 'start',
          title: {
            position: 'start',
            display: true,
            text: observationHour + ':00 READINGS',
          },
          labels: {
            textAlign: 'left',
            usePointStyle: true,
          },
        },
        annotation: {
          clip: false,
        },
      },
      interaction: {
        mode: 'depth',
      },
      elements: {
        point: {
          radius: 1, // 1 for single point
          hitRadius: 5,
        },
      },
    };
  }
};

const generateHSLColor = (idx, totalLines) => {
  // const hue = ((idx * 360) / totalLines + (idx % 360)) % 360;
  // const saturation = 50 + (idx % 3) * 15;
  // const lightness = 40 + (idx % 4) * 15;

  const hue = ((idx * 360) / totalLines) % 360; // Spread hues evenly across the total number of lines
  const saturation = 70; // high saturation to generate vivid colors
  const lightness = 20 + (idx * 50) / totalLines; // Start with 20% lightness and get lighter

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

export const buildFrostProbeGraphData = (observations, showInvalidData, totalLines = 28) => {
  if (observations) {
    return Object.keys(observations).map((e, idx) => {
      const localeDate = new Date(parseInt(e)).toLocaleDateString();
      const curDataItem = {
        type: 'line',
        //yAxisID: `yAxis-FP`, //frost probe can always use the same axis
        label: localeDate,
        data: [],
        id: idx,
        minBarLength: 7,
        observationTypeId: 542, // e.DatasetMeta.ObservationType.Id,
        yAxisList: null,
        datasetName: localeDate, //this is the date
        spanGaps: false,
        unitLabel: temperatureUnit,
      };

      // curDataItem.backgroundColor = graphColors[idx % graphColors.length];
      // curDataItem.borderColor = graphColors[idx % graphColors.length];

      // Dynamically generate the color for this line
      const color = generateHSLColor(idx, totalLines);
      curDataItem.backgroundColor = color;
      curDataItem.borderColor = color;

      if (!observations || isValueEmpty(observations) || !observations[e] || isValueEmpty(observations[e]))
        return curDataItem;

      curDataItem.data = observations[e].map((point) => {
        const isNan = Number.isNaN(point.Value) || point.Value === 'NAN';
        //value is only shown if it's invalid and show invalid data is checked
        let value = !point.IsInvalid || showInvalidData ? point.Value : null;
        //setup our display copy
        let valueCopy = value;
        //if value isNan set it to null but leave our display copy alone
        value = isNan ? null : value;
        valueCopy = isNan && showInvalidData ? point.Value : valueCopy;

        //setup the formatting for value
        if (value !== null && point.DisplayFormat?.length > 0) {
          value = numFormat(parseFloat(point.Value), point.DisplayFormat);
          valueCopy = value;
        }

        if (point.elevation) {
          return {
            x: value,
            y: point.elevation / 10,
            originalY: point.Value,
            displayY: valueCopy,
            isValid: isNan ? true : !point.IsInvalid,
            isNan: isNan,
            isMissing: !point.Value,
            backgroundColor: curDataItem.backgroundColor,
            borderColor: curDataItem.borderColor,
          };
        }
      });

      //removed undefined elements
      curDataItem.data = curDataItem.data.filter((d) => d !== undefined);
      return curDataItem;
    });
  }
};

export const normalizeFrostProbeObservations = (frostProbeObservationData, frostProbeDatasets, observationTime) => {
  //this is an array of layernumber & elevation, we use this later to determine
  // if any layers are missing and fill them so they show as gaps on the graph
  const layers = frostProbeDatasets
    .map((ds) => {
      return {
        layerNumber: ds.DatasetMeta.SensorPlacementInfo.SensorLayerNumber,
        elevation: ds.DatasetMeta.SensorPlacementInfo.RelativeElevationMillimetres,
      };
    })
    .reduce((acc, cur) => acc.concat(cur), [])
    .filter((val, idx, self) => self.indexOf(val) === idx);

  //take the observation data and fill any missing timestamps based on adjoining datasets
  const filledObsData = fillObservationData(frostProbeObservationData, 'TimeStamp');

  // take the observation data and add in the elevation, filter by the observation time
  // and finally group the results by TimeStamp
  const filteredFPData = Object.keys(filledObsData)
    .map((key) => {
      const result = [];

      filledObsData[key]
        .filter((val) => new Date(val.TimeStamp * 1000).getHours() == observationTime)
        .forEach((val) => {
          //map some of the external dataset.sensorPlacement values out
          const fbData = frostProbeDatasets.map((f) => {
            return {
              Id: f.Id,
              elevation: f.DatasetMeta.SensorPlacementInfo.RelativeElevationMillimetres,
              sensorLayerNumber: f.DatasetMeta.SensorPlacementInfo.SensorLayerNumber,
            };
          });

          result.push({
            ...val,
            elevation: fbData.find((f) => f.Id == key).elevation || 0,
            sensorLayerNumber: fbData.find((f) => f.Id == key).sensorLayerNumber || null,
          });
        });
      return [key, groupBy(result, 'TimeStamp')];
    })
    .reduce(mapArrayToObjectReducer, {});

  // transpose our data into it's final form
  // "<timestamp>": { <observation data> }
  let frostProbeObservations = {};

  Object.keys(filteredFPData).map((k) => {
    Object.keys(filteredFPData[k]).map((y) => {
      frostProbeObservations[y] = frostProbeObservations[y] ?? [];
      frostProbeObservations[y].push(filteredFPData[k][y][0]);
    });
  });

  //we have our frost probe data transposed, lets look for missing
  // sensor layers
  let missing = null;

  Object.keys(frostProbeObservations).map((k) => {
    missing = layers.filter(
      (o) =>
        !frostProbeObservations[k].reduce((obj, o) => ((obj[o.sensorLayerNumber] = o.sensorLayerNumber), obj), [])[
          o.layerNumber
        ],
    );

    if (missing.length > 0) {
      missing.map((m) => {
        frostProbeObservations[k].push({
          Value: null,
          TimeStamp: parseInt(k),
          IsValid: true,
          DisplayedMeaning: null,
          DisplayOrder: null,
          AssociatedValue: null,
          elevation: m.elevation,
          sensorLayerNumber: m.layerNumber,
        });
      });
    }

    return frostProbeObservations;
  });

  //almost done with our frostprobe data setup, sort by elevation
  Object.keys(frostProbeObservations).map((k) => {
    frostProbeObservations[k].sort((a, b) =>
      Math.abs(a.elevation) < Math.abs(b.elevation) ? 1 : Math.abs(b.elevation) < Math.abs(a.elevation) ? -1 : 0,
    );
  });
};
